import React from 'react';
import { useTranslation } from 'react-i18next';
import { NotificationSettings } from '../../../types/event';
import Slider from '../misc/Slider';
import eventUtils from '../../../utils/eventUtils';

interface EventNotificationFormProps {
  isDatePicker: boolean;
  onChange: (notificationSettings: NotificationSettings) => void;
  value?: NotificationSettings;
}

enum NotificationLevel {
  BASIC = 'basic',
  MEDIUM = 'medium',
  ALL = 'all',
}

const DEFAULT_NOTIFICATION_LEVEL = NotificationLevel.MEDIUM;

export default function EventNotificationForm({
  isDatePicker,
  onChange,
  value,
}: EventNotificationFormProps) {
  const { t } = useTranslation();
  const [notificationSettings, setNotificationSettings] = React.useState<NotificationSettings>(
    value || eventUtils.getBasicNotifications(isDatePicker),
  );
  const [notificationLevel, setNotificationLevel] = React.useState<NotificationLevel>(
    DEFAULT_NOTIFICATION_LEVEL,
  );

  React.useEffect(() => {
    switch (notificationLevel) {
      case NotificationLevel.BASIC:
        setNotificationSettings(eventUtils.getBasicNotifications(isDatePicker));
        break;
      case NotificationLevel.MEDIUM:
        setNotificationSettings(eventUtils.getMediumNotifications(isDatePicker));
        break;
      case NotificationLevel.ALL:
        setNotificationSettings(eventUtils.getAllNotifications(isDatePicker, true));
        break;
      default:
        setNotificationSettings(eventUtils.getMediumNotifications(isDatePicker));
    }
  }, [notificationLevel]);

  React.useEffect(() => {
    if (!value) return;

    if (Object.keys(value).length === 0) setNotificationLevel(DEFAULT_NOTIFICATION_LEVEL);

    if (deepEqual(value, eventUtils.getBasicNotifications(isDatePicker))) {
      setNotificationLevel(NotificationLevel.BASIC);
    } else if (deepEqual(value, eventUtils.getMediumNotifications(isDatePicker))) {
      setNotificationLevel(NotificationLevel.MEDIUM);
    } else if (deepEqual(value, eventUtils.getAllNotifications(isDatePicker, true))) {
      setNotificationLevel(NotificationLevel.ALL);
    }
  }, []);

  React.useEffect(() => {
    onChange(notificationSettings);
  }, [notificationSettings]);

  function isObject(objValue: any): objValue is object {
    return objValue !== null && typeof objValue === 'object';
  }

  function deepEqual(object1: object, object2: object): boolean {
    const keys1 = Object.keys(object1);
    const keys2 = Object.keys(object2);

    if (keys1.length !== keys2.length) {
      return false;
    }

    return keys1.every((key) => {
      const val1 = (object1 as any)[key];
      const val2 = (object2 as any)[key];
      const areObjects = isObject(val1) && isObject(val2);
      return areObjects ? deepEqual(val1, val2) : val1 === val2;
    });
  }

  const getNotificationDescription = (level: NotificationLevel): string => {
    switch (level) {
      case NotificationLevel.BASIC:
        return t('form.notificationSettings.levels.explainBasic');
      case NotificationLevel.MEDIUM:
        return t('form.notificationSettings.levels.explainMedium');
      case NotificationLevel.ALL:
        return t('form.notificationSettings.levels.explainAll');
      default:
        return '';
    }
  };

  return (
    <div className="flex w-full flex-col gap-4">
      <h2 className="font-medium text-primary-900">{t(`form.notificationSettings.title`)}</h2>
      <Slider
        items={[
          {
            label: t('form.notificationSettings.levels.basic'),
            value: NotificationLevel.BASIC,
          },
          {
            label: t('form.notificationSettings.levels.medium'),
            value: NotificationLevel.MEDIUM,
          },
          {
            label: t('form.notificationSettings.levels.all'),
            value: NotificationLevel.ALL,
          },
        ]}
        onChange={(level) => {
          setNotificationLevel(level);
        }}
        value={notificationLevel}
      />
      <h3 className="text-base font-normal text-primary-900 opacity-50">
        {getNotificationDescription(notificationLevel)}
      </h3>
    </div>
  );
}
